import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Tooltip, Divider, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import { Article, Nomenclature } from '../../models/models';
import {
  fetchNomenclatureByParent, createOrUpdateNomenclature,
  deleteNomenclature, clearMessages,
} from '../../reduxStore/NomenclatureSlice';
import { useSnackbar } from '../SnackbarProvider';
import ArticleAutoC from '../AutoComplete/ArticleAutoC';
import MyButton from '../MyButton';
import { ButtonType } from '../../constants/enums';

/** Item local en édition — utilise `id` négatif pour les nouveaux non-encore persistés. */
type DraftItem = Nomenclature & { _localKey: number };

interface NomenclatureEditorProps {
  /** Article parent dont on édite la nomenclature. Obligatoire. */
  parent: Article;
}

/**
 * Éditeur de nomenclature (recette de fabrication) d'un article donné.
 * Utilisé en dialog depuis GestionArticle.
 */
const NomenclatureEditor: React.FC<NomenclatureEditorProps> = ({ parent }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { error, successMessage } = useSelector((state: RootState) => state.NomenclatureSlice);

  // Snapshot des items au chargement : id → qty d'origine. Permet de détecter les modifs de qté.
  const [originalSnapshot, setOriginalSnapshot] = useState<Map<number, number>>(new Map());
  const [editList, setEditList]            = useState<DraftItem[]>([]);
  const [newComposant, setNewComposant]    = useState<Article | null>(null);
  const [newQuantite, setNewQuantite]      = useState<number>(1);
  const [loadingList, setLoadingList]      = useState(false);
  const [saving, setSaving]                = useState(false);

  const localKeyCounter = useRef(-1);

  useEffect(() => {
    if (successMessage) { showSnackbar(successMessage, 'success'); dispatch(clearMessages()); }
    if (error)          { showSnackbar(error, 'error');            dispatch(clearMessages()); }
  }, [successMessage, error, dispatch, showSnackbar]);

  const applyServerRows = (rows: Nomenclature[]) => {
    setEditList(rows.map((r) => ({ ...r, _localKey: r.id ?? --localKeyCounter.current })));
    const snap = new Map<number, number>();
    for (const r of rows) {
      if (r.id) snap.set(r.id, Number(r.quantite_consommee));
    }
    setOriginalSnapshot(snap);
  };

  useEffect(() => {
    if (!parent?.id) return;
    setLoadingList(true);
    dispatch(fetchNomenclatureByParent(parent.id))
      .unwrap()
      .then(applyServerRows)
      .catch(() => { setEditList([]); setOriginalSnapshot(new Map()); })
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent?.id]);

  const reload = async () => {
    if (!parent?.id) return;
    setLoadingList(true);
    try {
      const rows = await dispatch(fetchNomenclatureByParent(parent.id)).unwrap();
      applyServerRows(rows);
    } finally {
      setLoadingList(false);
    }
  };

  const handleAddLocal = () => {
    if (!newComposant?.id)            { showSnackbar('Choisissez un composant.',     'warning'); return; }
    if (newComposant.id === parent.id){ showSnackbar('Le composant ne peut pas être l\'article lui-même.', 'error'); return; }
    if (newQuantite <= 0)             { showSnackbar('Quantité doit être > 0.',      'warning'); return; }
    if (editList.some((c) => c.article_composant_id === newComposant.id)) {
      showSnackbar('Ce composant est déjà dans la liste.', 'warning'); return;
    }

    const localKey = --localKeyCounter.current;
    setEditList((prev) => [...prev, {
      _localKey:            localKey,
      id:                   undefined,
      article_parent_id:    parent.id!,
      article_composant_id: newComposant.id!,
      quantite_consommee:   newQuantite,
      article_composant:    {
        id:          newComposant.id!,
        code:        newComposant.code,
        designation: newComposant.designation,
        unite:       newComposant.unite,
      },
    } as DraftItem]);
    setNewComposant(null);
    setNewQuantite(1);
  };

  const handleQtyChange = (localKey: number, qty: number) => {
    setEditList((prev) => prev.map((c) =>
      c._localKey === localKey ? { ...c, quantite_consommee: qty } : c
    ));
  };

  const handleRemoveLocal = (localKey: number) => {
    setEditList((prev) => prev.filter((c) => c._localKey !== localKey));
  };

  const dirty = useMemo(() => {
    const editIds = new Set(editList.map((c) => c.id).filter((x): x is number => Boolean(x)));
    const hasNew      = editList.some((c) => !c.id);
    const hasDeleted  = Array.from(originalSnapshot.keys()).some((id) => !editIds.has(id));
    const hasQtyChange = editList.some((c) => {
      if (!c.id) return false;
      const orig = originalSnapshot.get(c.id);
      return orig !== undefined && Number(c.quantite_consommee) !== orig;
    });
    return hasNew || hasDeleted || hasQtyChange;
  }, [editList, originalSnapshot]);

  const handleSave = async () => {
    if (editList.some((c) => c.quantite_consommee <= 0)) {
      showSnackbar('Toutes les quantités doivent être > 0.', 'error');
      return;
    }

    setSaving(true);
    try {
      const editIds = new Set(editList.map((c) => c.id).filter((x): x is number => Boolean(x)));
      const toDelete = Array.from(originalSnapshot.keys()).filter((id) => !editIds.has(id));
      for (const id of toDelete) {
        await dispatch(deleteNomenclature(id));
      }

      for (const c of editList) {
        await dispatch(createOrUpdateNomenclature({
          id:                   c.id ?? -1,
          article_parent_id:    c.article_parent_id,
          article_composant_id: c.article_composant_id,
          quantite_consommee:   c.quantite_consommee,
        }));
      }

      showSnackbar('Nomenclature enregistrée.', 'success');
      await reload();
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erreur lors de la sauvegarde.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Composants requis pour 1 unité de {parent.code}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {parent.designation}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${editList.length} composant(s)`} size="small" />
          {dirty && <Chip label="Modifications non sauvegardées" size="small" color="warning" />}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {loadingList ? (
        <Typography color="text.secondary">Chargement…</Typography>
      ) : editList.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          Aucun composant déclaré. Ajoute la première ligne ci-dessous.
        </Typography>
      ) : (
        editList.map((c) => (
          <Box key={c._localKey} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #eee' }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600}>
                {c.article_composant?.code} — {c.article_composant?.designation}
                {!c.id && <Chip label="nouveau" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.article_composant?.unite ? `Unité : ${c.article_composant.unite}` : ''}
              </Typography>
            </Box>
            <TextField
              type="number"
              size="small"
              value={c.quantite_consommee}
              onChange={(e) => handleQtyChange(c._localKey, Number(e.target.value))}
              inputProps={{ min: 0.0001, step: 0.01, style: { textAlign: 'right', width: 90 } }}
              label="Qté / unité"
            />
            <Tooltip title="Retirer ce composant">
              <IconButton size="small" onClick={() => handleRemoveLocal(c._localKey)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))
      )}

      <Divider sx={{ my: 2 }} />

      {/* Formulaire d'ajout — local */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Ajouter un composant</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <ArticleAutoC
            key={editList.length}
            onArticleChange={setNewComposant}
            selectedArticle={newComposant}
          />
        </Box>
        <TextField
          type="number"
          size="small"
          value={newQuantite}
          onChange={(e) => setNewQuantite(Number(e.target.value))}
          inputProps={{ min: 0.0001, step: 0.01, style: { textAlign: 'right', width: 90 } }}
          label="Qté / unité"
        />
        <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleAddLocal}>
          <AddIcon /> Ajouter
        </MyButton>
      </Box>

      {/* Bouton Sauvegarder global */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <MyButton variant="outlined" onClick={reload} disabled={saving}>
          Annuler les modifs
        </MyButton>
        <MyButton
          actionType={ButtonType.DEFAULT}
          variant="contained"
          onClick={handleSave}
          loading={saving}
        >
          Sauvegarder
        </MyButton>
      </Box>
    </Paper>
  );
};

export default NomenclatureEditor;
